import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  FileCheck2, Sparkles, Check, Loader2, Lock, Database, ArrowRight,
  Zap, Users, ShieldCheck, Upload, FileText, GitBranch, Download,
  Target, Clock, Eye, Layers, ChevronRight, AlertTriangle
} from 'lucide-react';

const validateEmail = (email) => {
  const trimmed = email.trim();
  if (!trimmed) return 'Email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'Please enter a valid email address';
  return '';
};

const validatePassword = (password) => {
  if (!password) return 'Password is required';
  return '';
};

// --- Reusable section wrapper ---
const Section = ({ children, className = '', dark = false, id }) => (
  <section id={id} className={`py-16 sm:py-20 lg:py-24 ${dark ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'} ${className}`}>
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

const SectionHeading = ({ children, sub, light = false }) => (
  <div className="text-center mb-12 lg:mb-16">
    <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-heading font-bold tracking-tight ${light ? 'text-white' : 'text-slate-900'}`}>{children}</h2>
    {sub && <p className={`mt-3 text-base sm:text-lg max-w-2xl mx-auto ${light ? 'text-blue-200/80' : 'text-slate-500'}`}>{sub}</p>}
  </div>
);

// --- Login form card (exported for reuse in dedicated /login page) ---
export const LoginForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });

  useEffect(() => {
    const verified = searchParams.get('verified');
    if (verified === 'success') toast.success('Email verified successfully! You can now log in.');
    else if (verified === 'expired') toast.error('Verification link has expired. Please request a new one.');
    else if (verified === 'invalid') toast.error('Invalid verification link.');
  }, [searchParams]);

  const errors = useMemo(() => ({
    email: touched.email ? validateEmail(email) : '',
    password: touched.password ? validatePassword(password) : '',
  }), [email, password, touched]);

  const isFormValid = useMemo(() => !validateEmail(email) && !validatePassword(password), [email, password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    setLoginError('');
    if (!isFormValid) return;
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      const errorData = error.response?.data;
      let msg = 'Login failed';
      if (errorData?.detail) {
        msg = Array.isArray(errorData.detail) ? (errorData.detail[0]?.msg || 'Validation error') : errorData.detail;
      }
      setLoginError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md p-8 sm:p-10 bg-white rounded-2xl shadow-xl shadow-slate-200/50 border-0" data-testid="login-card">
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="relative p-2.5 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-lg shadow-blue-600/30">
            <FileCheck2 className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 tracking-tight">TestGenie AI</h1>
        </div>
        <p className="text-slate-800 text-sm font-medium">AI-Powered QA Documentation Platform</p>
        <p className="text-slate-500 text-xs mt-1">Trusted by QA professionals to improve coverage, reduce ambiguity, and accelerate release readiness.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {loginError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="login-error-message">
            <p className="text-sm text-red-600 text-center">{loginError}</p>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-slate-700" data-testid="login-email-label">Email Address</Label>
          <Input id="email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setLoginError(''); }}
            onBlur={() => setTouched(p => ({ ...p, email: true }))} placeholder="you@company.com"
            className={`h-12 px-4 rounded-lg transition-all placeholder:text-slate-400 ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20'} focus:ring-4`}
            data-testid="login-email-input" />
          {errors.email && <p className="text-sm text-red-500 mt-1" data-testid="login-email-error">{errors.email}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium text-slate-700" data-testid="login-password-label">Password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
            onBlur={() => setTouched(p => ({ ...p, password: true }))} placeholder="Enter your password"
            className={`h-12 px-4 rounded-lg transition-all placeholder:text-slate-400 ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20'} focus:ring-4`}
            data-testid="login-password-input" />
          {errors.password && <p className="text-sm text-red-500 mt-1" data-testid="login-password-error">{errors.password}</p>}
        </div>
        <Button type="submit" disabled={loading || !isFormValid}
          className="w-full h-12 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 hover:from-slate-800 hover:via-slate-700 hover:to-slate-800 text-white font-semibold rounded-xl shadow-lg shadow-slate-900/20 hover:shadow-xl transition-all duration-300 active:scale-[0.98] group disabled:opacity-50"
          data-testid="login-submit-button">
          {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Signing in...</> : <>Continue to Dashboard<ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" /></>}
        </Button>
        <div className="flex items-center justify-center gap-4 text-xs text-slate-400 mt-2">
          <span>No credit card required</span>
          <span className="w-1 h-1 rounded-full bg-slate-300" />
          <span>Your data is secure and private</span>
        </div>
        <div className="text-right mt-3">
          <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline" data-testid="forgot-password-link">Forgot Password?</Link>
        </div>
      </form>

      <div className="mt-6 flex flex-col items-center gap-2">
        <div className="flex items-center justify-center gap-5 text-xs text-slate-400">
          <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />Enterprise-grade security</span>
          <span className="flex items-center gap-1.5"><Database className="h-3.5 w-3.5" />Data isolated per project</span>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" />No data shared with third parties</span>
      </div>
      <p className="mt-6 text-center text-sm text-slate-500">
        Don't have an account?{' '}
        <Link to="/signup" className="text-blue-600 hover:text-blue-700 font-semibold hover:underline" data-testid="signup-link">Sign up</Link>
      </p>
    </Card>
  );
};

// ==================== MAIN PAGE ====================
export const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" data-testid="landing-page">

      {/* Top nav */}
      <nav className="absolute top-0 left-0 right-0 z-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg">
              <FileCheck2 className="h-4 w-4 text-white" />
            </div>
            <span className="font-heading font-bold text-lg text-white">TestGenie AI</span>
          </div>
          <button onClick={() => navigate('/pricing')} className="text-sm text-blue-200 hover:text-white font-medium transition-colors" data-testid="nav-pricing-link">
            Pricing
          </button>
        </div>
      </nav>

      {/* ===== 1. HERO SECTION ===== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950">
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill='%23ffffff'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        <div className="absolute top-1/4 right-1/4 w-80 h-80 bg-blue-500/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 left-1/6 w-96 h-96 bg-indigo-500/15 rounded-full blur-[140px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            {/* Left — Copy */}
            <div className="flex-1 text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white tracking-tight leading-tight mb-4" data-testid="hero-heading">
                Reduce Requirement Ambiguity & Improve Test Coverage
                <span className="text-blue-300"> with AI</span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-blue-100/80 mb-6 max-w-xl mx-auto lg:mx-0 leading-relaxed" data-testid="hero-subheading">
                Transform requirements into scenarios, test cases, traceability matrices, and release-ready QA documentation while uncovering gaps before testing begins.
              </p>
              <div className="flex flex-col gap-2 mb-8 max-w-xl mx-auto lg:mx-0" data-testid="hero-trust-points">
                {[
                  'Detect requirement gaps early',
                  'Improve requirement-to-test traceability',
                  'Generate business, edge, and negative scenarios',
                ].map((point, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                    <span className="text-blue-100/90 text-sm sm:text-base">{point}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Button onClick={() => navigate('/signup')}
                  className="h-12 px-6 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl shadow-lg shadow-blue-600/30 text-base gap-2"
                  data-testid="hero-cta-primary">
                  <Zap className="h-4 w-4" />Start Free QA Analysis
                </Button>
                <Button variant="outline" onClick={() => navigate('/signup')}
                  className="h-12 px-6 border-white/20 text-white hover:bg-white/10 rounded-xl text-base gap-2"
                  data-testid="hero-cta-secondary">
                  <Upload className="h-4 w-4" />Upload Your Requirements
                </Button>
              </div>
            </div>
            {/* Right — Login form */}
            <div className="flex-shrink-0 w-full lg:w-auto">
              <LoginForm />
            </div>
          </div>
        </div>
      </section>

      {/* ===== 2. PROBLEM SECTION ===== */}
      <Section id="problem">
        <SectionHeading sub="Most AI tools generate test cases. QA teams still struggle with requirement gaps, business coverage, and release confidence.">
          AI Can Generate Test Cases. But Can It Find What&apos;s Missing?
        </SectionHeading>
        <div className="max-w-2xl mx-auto space-y-4">
          {[
            'Ambiguous requirements causing defects',
            'Missing business-critical scenarios',
            'Gaps between requirements and test coverage',
            'Unclear release readiness',
            'Regression risks hidden across modules',
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-4 bg-red-50/60 border border-red-100 rounded-lg" data-testid={`problem-item-${i}`}>
              <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-slate-700 text-base">{item}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== 3. SOLUTION SECTION ===== */}
      <Section dark id="solution">
        <SectionHeading light sub="Identify gaps, improve coverage, and generate release-ready QA documentation.">
          From Requirements to Release Confidence
        </SectionHeading>
        <div className="max-w-3xl mx-auto">
          {[
            { icon: Upload, text: 'Analyze requirements automatically' },
            { icon: AlertTriangle, text: 'Identify requirement gaps and ambiguities' },
            { icon: Layers, text: 'Generate business, edge, negative, and validation scenarios' },
            { icon: Sparkles, text: 'Generate traceable test cases with expected results' },
            { icon: GitBranch, text: 'Measure requirement coverage with RTM' },
            { icon: FileCheck2, text: 'Prepare release-ready QA documentation' },
          ].map((step, i) => (
            <div key={i} className="flex items-center gap-4 py-4 border-b border-white/10 last:border-0" data-testid={`solution-step-${i}`}>
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <step.icon className="h-5 w-5 text-blue-400" />
              </div>
              <div className="flex items-center gap-3 flex-1">
                <span className="text-xs font-mono text-blue-400 bg-blue-400/10 px-2 py-0.5 rounded">Step {i + 1}</span>
                <span className="text-blue-50 text-base">{step.text}</span>
              </div>
              {i < 4 && <ChevronRight className="h-4 w-4 text-blue-400/40 flex-shrink-0 hidden sm:block" />}
            </div>
          ))}
        </div>
      </Section>

      {/* ===== 4. FEATURES SECTION ===== */}
      <Section id="features">
        <SectionHeading sub="Comprehensive QA tooling — no switching between apps.">
          Everything a QA Needs — In One Place
        </SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: FileText, title: 'Requirement Analysis', desc: 'AI parses documents into structured, testable requirements.' },
            { icon: Layers, title: 'Scenario Coverage Generation', desc: 'Positive, negative, edge & validation scenarios per requirement.' },
            { icon: Sparkles, title: 'Traceable Test Case Generation', desc: 'Detailed steps, expected results, and preconditions — instantly.' },
            { icon: GitBranch, title: 'Coverage & Traceability Matrix', desc: 'Requirement → Scenario → Test Case hierarchy with coverage %.' },
            { icon: Download, title: 'Jira / Zephyr Export', desc: 'One-click export to Excel or Jira/Zephyr Scale compatible CSV.' },
            { icon: Target, title: 'Requirement Gap Detection', desc: 'Automatically find uncovered scenarios and generate missing tests.' },
          ].map((f, i) => (
            <Card key={i} className="p-6 border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all" data-testid={`feature-card-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-1.5">{f.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* ===== 5. VALUE SECTION ===== */}
      <Section dark id="value" className="bg-gradient-to-br from-blue-950 to-indigo-950">
        <SectionHeading light>Save 80% of Your QA Effort</SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {[
            { icon: Clock, text: 'Reduce Manual QA Effort', desc: 'Requirement analysis and documentation completed in minutes.' },
            { icon: Eye, text: 'Improve Coverage Confidence', desc: 'Detect missing scenarios before execution begins.' },
            { icon: AlertTriangle, text: 'Catch Requirement Gaps Early', desc: 'Identify ambiguities and uncovered requirements.' },
            { icon: Zap, text: 'Release Faster', desc: 'Ship with higher confidence and better traceability.' },
          ].map((v, i) => (
            <div key={i} className="flex items-start gap-4 p-5 bg-white/5 border border-white/10 rounded-xl" data-testid={`value-item-${i}`}>
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <v.icon className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-base mb-1">{v.text}</h3>
                <p className="text-blue-200/70 text-sm">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== 6. TARGET USERS ===== */}
      <Section id="users">
        <SectionHeading sub="Designed for professionals who value speed and coverage.">Built for QA Professionals Who Want Better Coverage</SectionHeading>
        <div className="flex flex-wrap justify-center gap-4">
          {[
            { icon: Users, label: 'QA Engineers' },
            { icon: Target, label: 'QA Leads' },
            { icon: FileCheck2, label: 'Freelancers' },
            { icon: Zap, label: 'Automation Testers' },
          ].map((u, i) => (
            <div key={i} className="flex items-center gap-3 px-6 py-4 bg-slate-50 border border-slate-100 rounded-xl" data-testid={`user-type-${i}`}>
              <u.icon className="h-5 w-5 text-blue-600" />
              <span className="text-slate-800 font-medium text-base">{u.label}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== 6.5 SOCIAL PROOF ===== */}
      <Section dark id="social-proof" className="bg-gradient-to-br from-slate-900 to-blue-950">
        <SectionHeading light>Trusted by QA Professionals</SectionHeading>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-4xl mx-auto">
          {[
            { value: '3972+', label: 'Requirements Processed' },
            { value: '9668+', label: 'Scenarios Generated' },
            { value: '6938+', label: 'Test Cases Created' },
            { value: '134+',  label: 'QA Signups' },
          ].map((s, i) => (
            <div key={i} className="text-center p-5 sm:p-6 bg-white/5 border border-white/10 rounded-xl" data-testid={`social-proof-${i}`}>
              <p className="text-3xl sm:text-4xl font-heading font-bold text-white tracking-tight">{s.value}</p>
              <p className="text-blue-200/70 text-sm mt-1.5">{s.label}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== 7. FINAL CTA ===== */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-center" id="cta">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-white tracking-tight mb-4" data-testid="final-cta-heading">
            Start Generating Test Cases in Seconds
          </h2>
          <p className="text-blue-100/80 text-base sm:text-lg mb-8">No credit card required. Free plan available.</p>
          <Button onClick={() => navigate('/signup')}
            className="h-12 px-8 bg-white text-blue-700 hover:bg-blue-50 font-semibold rounded-xl shadow-lg text-base gap-2"
            data-testid="final-cta-button">
            <Sparkles className="h-4 w-4" />Try TestGenie Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 text-center space-y-2">
        <p className="text-sm text-slate-400 italic" data-testid="founder-credibility">Built by a QA engineer with 15+ years of real-world testing experience.</p>
        <p className="text-sm text-slate-500">TestGenie AI — AI-powered QA documentation for modern teams.</p>
      </footer>
    </div>
  );
};
